import { useState } from 'react'

import { G, bandElongationMm, oscStep, ru } from '../../lib/physics'
import { Slider, useFrames } from './common'

/** Резиновая нить и грузы: закон Гука и его граница (урок 34). */

interface Строка {
  m: number
  f: number
  dl: number
  прогноз: number | null
}

const MM_PX = 1.4
const ZERO_Y = 70

export default function BandLab() {
  const [k, setK] = useState(25)
  const [наСтоле, setНаСтоле] = useState(10) // какой груз выбран
  const [наЧашке, setНаЧашке] = useState(0) // какой груз висит
  const [прогноз, setПрогноз] = useState('')
  const [строки, setСтроки] = useState<Строка[]>([])
  const [итог, setИтог] = useState<string | null>(null)
  const [вид, setВид] = useState({ dl: 0, v: 0 }) // удлинение на рисунке, мм — с колебанием

  const f = (наЧашке / 1000) * G
  const dl = bandElongationMm(f, k)

  useFrames((dt) => {
    setВид((s) => {
      const [y, v] = oscStep(s.dl, s.v, dl, (2 * Math.PI) / 0.8, 0.3, dt)
      return { dl: y, v }
    })
  })

  const положить = () => {
    const ff = (наСтоле / 1000) * G
    const новое = bandElongationMm(ff, k)
    const п = прогноз.trim() === '' ? null : Number(прогноз.replace(',', '.'))
    setНаЧашке(наСтоле)
    setСтроки((rows) => [...rows.filter((r) => r.m !== наСтоле), { m: наСтоле, f: ff, dl: новое, прогноз: п }]
      .sort((a, b) => a.m - b.m))
    const поЗакону = (ff / k) * 1000
    if (п !== null && Number.isFinite(п)) {
      const разница = Math.abs(п - новое)
      setИтог(
        разница <= 1
          ? `Прогноз ${ru(п, 0)} мм — опыт ${ru(новое, 0)} мм. Точно!`
          : `Прогноз ${ru(п, 0)} мм — опыт ${ru(новое, 0)} мм.` +
              (новое - поЗакону > 2 ? ' Нить растянулась больше, чем по закону Гука: деформация уже не малая.' : ''),
      )
    } else {
      setИтог(
        новое - поЗакону > 2
          ? `По закону Гука ждали ${ru(поЗакону, 0)} мм, а вышло ${ru(новое, 0)} мм — деформация уже не малая.`
          : null,
      )
    }
    setПрогноз('')
  }

  const сменитьНить = (v: number) => {
    setK(v)
    setСтроки([])
    setИтог(null)
    setНаЧашке(0)
  }

  const конецНити = ZERO_Y + Math.min(вид.dl, 150) * MM_PX
  const дел: number[] = []
  for (let mm = 0; mm <= 150; mm += 5) дел.push(mm)

  // график F(Δl)
  const gw = 300
  const gh = 170
  const dlMax = 150
  const fMax = 1.3
  const gx = (d: number) => 40 + (d / dlMax) * (gw - 50)
  const gy = (ff: number) => gh - 25 - (ff / fMax) * (gh - 40)
  const законДо = Math.min(fMax, (k * dlMax) / 1000)

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" viewBox="0 0 220 300" role="img" aria-label="Резиновая нить с чашкой и линейкой">
          <line x1={30} y1={10} x2={30} y2={290} className="ink-muted" strokeWidth={6} />
          <line x1={30} y1={12} x2={120} y2={12} className="ink-muted" strokeWidth={6} />
          <line x1={10} y1={292} x2={200} y2={292} className="ink-muted" strokeWidth={8} />
          <line x1={110} y1={12} x2={110} y2={конецНити} className="ink-band" strokeWidth={Math.max(1.2, 3 - вид.dl / 60)} />
          <line x1={150} y1={ZERO_Y} x2={150} y2={ZERO_Y + 150 * MM_PX} className="ink" />
          {дел.map((mm) => (
            <g key={mm}>
              <line x1={150} y1={ZERO_Y + mm * MM_PX} x2={mm % 10 === 0 ? 160 : 156} y2={ZERO_Y + mm * MM_PX} className="ink" />
              {mm % 20 === 0 && (
                <text x={164} y={ZERO_Y + mm * MM_PX + 3} className="lab-tick">{mm}</text>
              )}
            </g>
          ))}
          <text x={164} y={ZERO_Y - 8} className="lab-tick">мм</text>
          <line x1={110} y1={конецНити} x2={150} y2={конецНити} className="ink-el" strokeWidth={2} />
          <line x1={110} y1={конецНити} x2={110} y2={конецНити + 14} className="ink" />
          <line x1={84} y1={конецНити + 14} x2={136} y2={конецНити + 14} className="ink" strokeWidth={3} />
          {наЧашке > 0 && (
            <>
              <rect x={96} y={конецНити - 2} width={28} height={16} className="fill-body ink" />
              <text x={90} y={конецНити + 8} textAnchor="end" className="lab-label">{наЧашке} г</text>
            </>
          )}
        </svg>

        <div>
          <svg className="lab-svg" viewBox={`0 0 ${gw} ${gh}`} role="img" aria-label="График силы упругости от удлинения">
            <line x1={40} y1={gh - 25} x2={gw - 5} y2={gh - 25} className="ink" />
            <line x1={40} y1={10} x2={40} y2={gh - 25} className="ink" />
            {[0, 50, 100, 150].map((d) => (
              <text key={d} x={gx(d)} y={gh - 10} textAnchor="middle" className="lab-tick">{d}</text>
            ))}
            {[0, 0.5, 1].map((ff) => (
              <text key={ff} x={34} y={gy(ff) + 3} textAnchor="end" className="lab-tick">{ru(ff, 1)}</text>
            ))}
            <text x={gw - 5} y={gh - 28} textAnchor="end" className="lab-tick">Δl, мм</text>
            <text x={44} y={16} className="lab-tick">F упр, Н</text>
            {строки.length >= 2 && (
              <line
                x1={gx(0)} y1={gy(0)}
                x2={gx((законДо / k) * 1000)} y2={gy(законДо)}
                className="ink-muted" strokeDasharray="5 4"
              />
            )}
            {строки.map((r) => (
              <circle key={r.m} cx={gx(Math.min(r.dl, dlMax))} cy={gy(r.f)} r={4} className="fill-el" />
            ))}
          </svg>

          <table className="lab-table">
            <thead>
              <tr><th>m, г</th><th>F упр, Н</th><th>Δl, мм</th><th>прогноз</th></tr>
            </thead>
            <tbody>
              {строки.map((r) => (
                <tr key={r.m}>
                  <td>{r.m}</td>
                  <td>{ru(r.f, 2)}</td>
                  <td>{ru(r.dl, 0)}</td>
                  <td className="muted">{r.прогноз === null ? '—' : ru(r.прогноз, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {итог && <div className="lab-caption">{итог}</div>}

      <Slider label="Груз" value={наСтоле} min={5} max={120} step={5} unit="г" onChange={setНаСтоле} />
      <label className="lab-slider">
        <span>Твой прогноз удлинения, мм (необязательно)</span>
        <input inputMode="decimal" value={прогноз} placeholder="например, 8" onChange={(e) => setПрогноз(e.target.value)} />
      </label>
      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={положить}>Положить {наСтоле} г на чашку</button>
        <button className="btn" onClick={() => { setНаЧашке(0); setИтог(null) }}>Снять груз</button>
      </div>

      <Slider label="Жёсткость нити k" value={k} min={15} max={50} step={1} unit="Н/м" onChange={сменитьНить} />
      <p className="muted small">
        Сначала предскажи удлинение, потом положи груз. Малые грузы ложатся на прямую:
        F упр = k·|Δl|. Большой груз уводит точку с прямой — закон Гука верен только при
        малых упругих деформациях. Новая жёсткость — новая нить, таблица начинается заново.
      </p>
    </div>
  )
}
